/**
 * Canonical Claude Code OTel semantic-attribute key SSOT -- the `OtlpAttributedRecord.attributes[].key` spellings THIS
 * TREE READS. It is deliberately NOT an exhaustive catalogue of what the harness emits: the bag carries keys nothing
 * here looks up (`docs/operations/claude-code-harness.md` names `query_source` as one), and a reader wanting one adds
 * the member at that point, which is the cycle `QUAL-OTEL-ATTR-KEY-RAW-LITERAL-001`'s `fix` already directs. So a
 * member with no lookup is dead vocabulary rather than documentation of the wire -- absence of a member is never
 * evidence the wire key does not exist. A typo at either end returns `undefined` from `readAttribute`
 * (const/otlp-record-read.ts), which
 * downstream rollups read as legitimate absence rather than failing loudly. Naming follows the harness emitter contract
 * VERBATIM, mixing dotted and underscored forms; do NOT normalise.
 *
 * NO DEVELOPER-IDENTITY MEMBER LIVES HERE, and its absence is a security decision rather than an oversight: no member
 * names the wire spellings `dev.id` or `devId` for the ingest normalizer to dual-read. The door stamps `devId` from
 * the `CACHE` KV ENROLLMENT record the presented bearer resolves to (`utils/telemetry/enrollment-store.ts`), and ignores
 * whatever the payload claims -- so a vocabulary entry for the wire spelling would only ever be used to reintroduce
 * trust in a caller-supplied identity. Two fixtures pin that the trusted value wins: the planted `dev.id` value
 * 'ATTACKER_SUPPLIED' in `microservices/ai-telemetry-ingest/__tests__/flow-engine.worker-auth.test.ts`, and
 * `untrusted-otlp-devid` in the normalizer's suite.
 */
export const OTEL_ATTRS = {
	SESSION_ID: 'session.id',
	TOOL_NAME: 'tool_name',
	EVENT_TIMESTAMP: 'event.timestamp',
	/** Canonical event-kind attribute, preferred over a record-native `name`/body. */
	EVENT_NAME: 'event.name',
	/** Assistant identifier; values come from `TELEMETRY_ASSISTANTS` in `const/telemetry.ts`. */
	ASSISTANT: 'assistant',
	/** Failure message text on failed tool_result events. */
	ERROR: 'error',
	/** Failure classification on failed tool_result events (e.g. ShellError). */
	ERROR_TYPE: 'error_type',
	MODEL: 'model',
	COST_USD: 'cost_usd',
	INPUT_TOKENS: 'input_tokens',
	OUTPUT_TOKENS: 'output_tokens',
	CACHE_READ_TOKENS: 'cache_read_tokens',
	CACHE_CREATION_TOKENS: 'cache_creation_tokens',
	DURATION_MS: 'duration_ms',
	TOTAL_DURATION_MS: 'total_duration_ms',
	/** Time-to-first-token on api_request events. */
	TTFT_MS: 'ttft_ms',
	/** Terminal reason the model stopped generating, on api_request events. */
	STOP_REASON: 'stop_reason',
	/** Explicit status on records carrying NO boolean-shaped `success` attr (e.g. an `mcp_server_connection` failure). A query row's typed `status` field is projected from `success` only, so it reads null while the failure lives here in the raw bag. Compare against `OTEL_STATUS_FAILED`. */
	STATUS: 'status',
	/** HTTP status code on api_error events. */
	STATUS_CODE: 'status_code',
	SUCCESS: 'success',
	EFFORT: 'effort',
	TOOL_INPUT: 'tool_input',
	TOOL_PARAMETERS: 'tool_parameters',
	PROMPT: 'prompt',
	TOOL_RESULT_SIZE_BYTES: 'tool_result_size_bytes',
	HOOK_NAME: 'hook_name',
	HOOK_EVENT: 'hook_event',
	COMMAND: 'command',
	DECISION: 'decision',
	DECISION_SOURCE: 'source',
	SPAN_TYPE: 'span.type',
	TOOL_USE_ID: 'tool_use_id',
	/** Instrumentation-scope NAME, promoted onto the flat row by the ingest normalizer. Not a record attribute on the
	 *  wire -- it is `scope<Signal>s[].scope.name`, which identifies the EMITTER (`federa.flow-engine` for fleet spans
	 *  vs a developer collector's library name) and is the practical discriminator between the two producers in one
	 *  table. The key spelling follows the OTel collector convention for scope promotion, so a row exported onward
	 *  carries the name a downstream OTel reader already looks for. */
	SCOPE_NAME: 'otel.scope.name',
	/** Instrumentation-scope VERSION, promoted the same way from `scope<Signal>s[].scope.version`. */
	SCOPE_VERSION: 'otel.scope.version',
} as const;

/** Canonical string sentinel for a failed `success` attribute. The wire carries TWO forms: the current harness emits
 * the STRING `'false'`, but the OTLP normalizer preserves JSON booleans (`coerceAnyValue` in
 * const/otlp-record-read.ts), so stored bags can hold `false` proper -- the query SQL's failed-only
 * predicate matches both. `toQueryRow` normalizes the boolean form onto this sentinel at the projection boundary, so
 * every downstream comparison ('insights folds, client filters, drawer pills') compares against strings only. */
export const OTEL_SUCCESS_FALSE = 'false' as const;

/** The `status`-attribute counterpart to `OTEL_SUCCESS_FALSE`, stamped on a failed record carrying no `success` attr at all. Consumers compare against the raw attribute bag, since the typed `status` row field is null there. */
export const OTEL_STATUS_FAILED = 'failed' as const;

/** Stamped on span `decision`/`source` the harness could not resolve; treat as absent.
 *
 *  INBOUND, like its two siblings above: the instrumentation harness WRITES this token onto the span attribute bag and
 *  this repo only ever COMPARES against it -- `OTEL_ATTRS.DECISION` and `OTEL_ATTRS.DECISION_SOURCE` have NO writer in
 *  the tracked tree, every reference being a read under `spa/analytics/frontend/telemetry/` (re-probe rather than
 *  inherit that claim). So the value is not this repo's to choose, which is what separates it from the same-spelling
 *  OUTBOUND placeholder `SENTINEL_UNKNOWN` (`const/sentinels.ts`): that one is the string this repo EMITS at its
 *  `?? SENTINEL_UNKNOWN` fallback sites and may be re-spelled freely (`DEFAULT_APP_NAME` in `const/auth.ts` already
 *  derives from it on exactly that basis). Deriving this constant from that one would couple an inbound wire contract
 *  to an internal naming choice:
 *  re-spelling the placeholder would silently stop `resolveTelemetryDetail` recognising unresolved spans, so the
 *  telemetry drawer would render `'unknown'` as a real decision with every suite still green -- the same failure the
 *  `NO_LIMIT_LOWER` docstring in `const/sentinels.ts` records for a re-cased money sentinel. Two tokens, one spelling,
 *  two owners. */
export const OTEL_UNKNOWN_SENTINEL = 'unknown' as const;
